import React from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, Users, Clock, Star, Shield, Search } from 'lucide-react';
import { City } from '@/data/cities';
import { Link } from 'react-router-dom';
import { CityLanguage } from '@/context/languages/languageData';
import { useCityTranslations } from '@/hooks/useCityTranslations';

interface MassiveLocalizedCityHeroProps {
  city: City;
  language: CityLanguage;
}

const MassiveLocalizedCityHero: React.FC<MassiveLocalizedCityHeroProps> = ({ 
  city, 
  language 
}) => {
  const { translations, isLoading } = useCityTranslations(city, language);
  const cleanCityName = city.name.replace(/\s+\d+$/, '').trim();
  
  const getTranslation = (key: string, fallback: string = '') => {
    return translations[key] || fallback;
  };

  const formatPopulation = (population: number) => {
    if (population >= 1000000) {
      return `${(population / 1000000).toFixed(1)}M`;
    } else if (population >= 1000) {
      return `${(population / 1000).toFixed(0)}K`;
    }
    return population.toString();
  };

  const localizedText = {
    home: {
      en: 'Home', es: 'Inicio', fr: 'Accueil', de: 'Startseite', it: 'Home', pt: 'Início',
      zh: '首页', ja: 'ホーム', ko: '홈', ar: 'الرئيسية', hi: 'होम'
    }[language] || 'Home',
    cities: {
      en: 'Cities', es: 'Ciudades', fr: 'Villes', de: 'Städte', it: 'Città', pt: 'Cidades',
      zh: '城市', ja: '都市', ko: '도시', ar: 'المدن', hi: 'शहर'
    }[language] || 'Cities',
    headline: getTranslation('heroTitle', `Find Your Therapist in ${cleanCityName}`),
    subtitle: getTranslation('heroDescription', '150+ licensed professionals • Online & in-person • Sessions from $25'),
    howItWorks: getTranslation('howItWorksButton', 'How It Works')
  };

  return (
    <section className="bg-gradient-to-br from-primary/5 to-background py-8 md:py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb compacto */}
          <nav aria-label="Breadcrumb" className="mb-4 text-center">
            <ol className="flex items-center justify-center text-sm text-muted-foreground">
              <li>
                <Link to="/" className="hover:text-primary transition-colors">
                  {localizedText.home}
                </Link>
              </li>
              <span className="mx-2">/</span>
              <li>
                <Link to="/cities" className="hover:text-primary transition-colors">
                  {localizedText.cities}
                </Link>
              </li>
              <span className="mx-2">/</span>
              <li aria-current="page">
                <span className="text-foreground font-medium">{cleanCityName}</span>
              </li>
            </ol>
          </nav>

          <div className="text-center mb-8">
            {/* Location badge */}
            <div className="flex items-center justify-center mb-4">
              <MapPin className="h-5 w-5 text-primary mr-2" />
              <span className="text-lg font-medium text-muted-foreground">
                {cleanCityName}, {city.country}
              </span>
            </div>
            
            {/* Headline compacto y directo */}
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
              {localizedText.headline}
            </h1>
            
            {/* Subtitle simple con stats clave */}
            <p className="text-lg text-muted-foreground mb-6">
              {localizedText.subtitle}
            </p>
            
            {/* CTAs prominentes */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/find">
                <Button size="lg" className="w-full sm:w-auto px-8 text-base">
                  <Search className="h-5 w-5 mr-2" />
                  {getTranslation('findTherapistsButton', `Find Therapists in ${cleanCityName}`)}
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button variant="outline" size="lg" className="w-full sm:w-auto px-8 text-base">
                  {localizedText.howItWorks}
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Stats inline - UNA LÍNEA compacta */}
          <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground mb-6">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span>{formatPopulation(city.population)} population</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              <span>150+ therapists</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span>24/7 support</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-green-500" />
              <span>100% verified</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MassiveLocalizedCityHero;
